import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MapPin, Layers, TrendingUp, TrendingDown, AlertTriangle, Target, 
  DollarSign, Users, ShoppingCart, Car, Building, Eye, Zap, Calendar as CalendarIcon
} from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import AIInsightsChatbot from '../components/Dashboard/AIInsightsChatbot';
import dubaiMap from '../images/dubai.png';

const UAEOutdoorAdvertising = () => {
  const [selectedCity, setSelectedCity] = useState('Dubai');
  const [heatmapLayer, setHeatmapLayer] = useState('traffic'); // 'traffic', 'investment', 'revenue', 'orders'
  const [selectedObjective, setSelectedObjective] = useState('awareness'); // 'awareness', 'conversion', 'footfall'
  const [preset, setPreset] = useState('last_30');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  // Mock data for UAE cities with comprehensive outdoor advertising metrics
  const uaeOutdoorData = {
    Dubai: {
      totalSpend: 8500000,
      totalRevenue: 24600000,
      roi: 2.89,
      placements: [
        { 
          id: 1, name: 'Sheikh Zayed Road - Premium', type: 'Digital Billboard', 
          lat: 25.2048, lng: 55.2708, traffic: 185000, investment: 450000, revenue: 1200000,
          walkIns: 2400, homeOrders: 8900, premiumScore: 95, efficiency: 2.67
        },
        { 
          id: 2, name: 'Dubai Mall Entrance', type: 'LED Wall', 
          lat: 25.1972, lng: 55.2796, traffic: 125000, investment: 320000, revenue: 890000,
          walkIns: 4200, homeOrders: 6500, premiumScore: 92, efficiency: 2.78
        },
        { 
          id: 3, name: 'Business Bay Bridge', type: 'Static Billboard', 
          lat: 25.1879, lng: 55.2411, traffic: 95000, investment: 180000, revenue: 420000,
          walkIns: 1800, homeOrders: 3200, premiumScore: 78, efficiency: 2.33
        },
        { 
          id: 4, name: 'JBR Beach Walk', type: 'Digital Panel', 
          lat: 25.0777, lng: 55.1378, traffic: 68000, investment: 220000, revenue: 680000,
          walkIns: 3600, homeOrders: 2100, premiumScore: 85, efficiency: 3.09
        },
        { 
          id: 5, name: 'Al Khail Road', type: 'Highway Billboard', 
          lat: 25.1122, lng: 55.2053, traffic: 142000, investment: 280000, revenue: 520000,
          walkIns: 1200, homeOrders: 4800, premiumScore: 65, efficiency: 1.86
        }
      ],
      orderAreas: [
        { area: 'Downtown Dubai', orders: 8900, population: 25000, density: 0.356, premiumSites: 3 },
        { area: 'Dubai Marina', orders: 6500, population: 18000, density: 0.361, premiumSites: 2 },
        { area: 'Business Bay', orders: 4200, population: 22000, density: 0.191, premiumSites: 4 },
        { area: 'JBR', orders: 3600, population: 8500, density: 0.424, premiumSites: 1 },
        { area: 'DIFC', orders: 850, population: 12000, density: 0.071, premiumSites: 5 }
      ]
    },
    'Abu Dhabi': {
      totalSpend: 4200000,
      totalRevenue: 11800000,
      roi: 2.81,
      placements: [
        { 
          id: 6, name: 'Corniche Road Premium', type: 'Digital Billboard', 
          lat: 24.4539, lng: 54.3773, traffic: 95000, investment: 380000, revenue: 950000,
          walkIns: 2100, homeOrders: 5400, premiumScore: 88, efficiency: 2.50
        },
        { 
          id: 7, name: 'Sheikh Rashid Bin Saeed Street', type: 'LED Panel', 
          lat: 24.4648, lng: 54.3618, traffic: 78000, investment: 250000, revenue: 620000,
          walkIns: 1800, homeOrders: 3900, premiumScore: 75, efficiency: 2.48
        }
      ],
      orderAreas: [
        { area: 'Al Reem Island', orders: 5400, population: 15000, density: 0.360, premiumSites: 2 },
        { area: 'Al Bateen', orders: 3900, population: 8200, density: 0.476, premiumSites: 1 },
        { area: 'Al Khalidiyah', orders: 2100, population: 18500, density: 0.114, premiumSites: 3 }
      ]
    }
  };

  const currentCityData = uaeOutdoorData[selectedCity];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getHeatmapIntensity = (value, maxValue) => {
    const intensity = value / maxValue;
    if (intensity > 0.8) return 'from-red-600 to-red-700';
    if (intensity > 0.6) return 'from-orange-500 to-orange-600';
    if (intensity > 0.4) return 'from-yellow-500 to-yellow-600';
    if (intensity > 0.2) return 'from-blue-400 to-blue-500';
    return 'from-blue-200 to-blue-300';
  };

  const getEfficiencyBadge = (efficiency) => {
    if (efficiency > 2.5) return <Badge className="bg-green-100 text-green-800">High Efficiency</Badge>;
    if (efficiency > 2.0) return <Badge className="bg-yellow-100 text-yellow-800">Medium Efficiency</Badge>;
    return <Badge className="bg-red-100 text-red-800">Low Efficiency</Badge>;
  };

  const getPremiumRecommendation = (area) => {
    const orderDensity = area.orders / area.population;
    if (orderDensity < 0.15 && area.premiumSites > 0) {
      return {
        type: 'premium-opportunity',
        message: 'Low order density but premium locations available - consider brand awareness campaigns',
        action: 'Invest in premium placements for brand building'
      };
    } else if (orderDensity > 0.35) {
      return {
        type: 'high-conversion',
        message: 'High order density - optimize for conversion-focused campaigns',
        action: 'Increase performance marketing spend'
      };
    }
    return {
      type: 'balanced',
      message: 'Balanced performance - maintain current strategy',
      action: 'Continue current allocation'
    };
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' }).format(date);
  };

  const getPresetRange = (presetKey) => {
    const today = new Date();
    const end = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    let start = new Date(end);

    if (presetKey === 'last_7') {
      start.setDate(end.getDate() - 6);
    } else if (presetKey === 'last_30') {
      start.setDate(end.getDate() - 29);
    } else if (presetKey === 'this_month') {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (presetKey === 'last_month') {
      const firstOfThisMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      const lastMonthEnd = new Date(firstOfThisMonth.getTime() - 24 * 60 * 60 * 1000);
      start = new Date(lastMonthEnd.getFullYear(), lastMonthEnd.getMonth(), 1);
      return { from: start, to: lastMonthEnd };
    }
    return { from: start, to: end };
  };

  const [dateRange, setDateRange] = useState(() => getPresetRange('last_30'));

  const handlePresetChange = (value) => {
    setPreset(value);
    const range = getPresetRange(value);
    setDateRange(range);
  };

  const rangeLabel = dateRange?.from && dateRange?.to
    ? `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
    : 'Select date range';

  const maxTraffic = Math.max(...currentCityData.placements.map(p => p.traffic));
  const maxInvestment = Math.max(...currentCityData.placements.map(p => p.investment));
  const maxRevenue = Math.max(...currentCityData.placements.map(p => p.revenue));
  const maxOrders = Math.max(...currentCityData.orderAreas.map(a => a.orders));

  const getLayerValue = (placement) => {
    switch (heatmapLayer) {
      case 'traffic': return placement.traffic;
      case 'investment': return placement.investment;
      case 'revenue': return placement.revenue;
      case 'orders': return placement.walkIns + placement.homeOrders;
      default: return placement.traffic;
    }
  };

  const getLayerMax = () => {
    switch (heatmapLayer) {
      case 'traffic': return maxTraffic;
      case 'investment': return maxInvestment;
      case 'revenue': return maxRevenue;
      case 'orders': return Math.max(...currentCityData.placements.map(p => p.walkIns + p.homeOrders));
      default: return maxTraffic;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            UAE Outdoor Advertising Analytics
          </h1>
        <p className="text-gray-600">Comprehensive outdoor media performance analysis with density mapping and ROI optimization</p>
        <div className="mt-4 flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-10 px-4">
                <CalendarIcon className="h-4 w-4 mr-2" />
                {rangeLabel}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="start">
              <div className="p-3">
                <Calendar
                  mode="range"
                  numberOfMonths={2}
                  selected={dateRange}
                  onSelect={(range) => {
                    setDateRange(range || { from: null, to: null });
                  }}
                  initialFocus
                />
                <div className="flex justify-end gap-2 pt-3">
                  <Button variant="ghost" size="sm" onClick={() => setIsCalendarOpen(false)}>Cancel</Button>
                  <Button size="sm" onClick={() => setIsCalendarOpen(false)}>Apply</Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>

          <div className="min-w-[220px]">
            <Select value={preset} onValueChange={handlePresetChange}>
              <SelectTrigger aria-label="Date range preset">
                <SelectValue placeholder="Select preset" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last_7">Last 7 days</SelectItem>
                <SelectItem value="last_30">Last 30 days</SelectItem>
                <SelectItem value="this_month">This month</SelectItem>
                <SelectItem value="last_month">Last month</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        </div>

        {/* Controls */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Analysis Controls</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Select value={selectedCity} onValueChange={setSelectedCity}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Dubai">Dubai</SelectItem>
                  <SelectItem value="Abu Dhabi">Abu Dhabi</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={heatmapLayer} onValueChange={setHeatmapLayer}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="traffic">Traffic Density</SelectItem>
                  <SelectItem value="investment">Media Investment</SelectItem>
                  <SelectItem value="revenue">Revenue Generated</SelectItem>
                  <SelectItem value="orders">Order Volume</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedObjective} onValueChange={setSelectedObjective}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="awareness">Brand Awareness</SelectItem>
                  <SelectItem value="conversion">Conversion</SelectItem>
                  <SelectItem value="footfall">Footfall</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Tabs defaultValue="heatmap" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="heatmap">Density Heatmap</TabsTrigger>
            <TabsTrigger value="placements">Placement Analysis</TabsTrigger>
            <TabsTrigger value="delivery">Delivery Analytics</TabsTrigger>
            <TabsTrigger value="optimization">Optimization</TabsTrigger>
          </TabsList>

          <TabsContent value="heatmap" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Interactive Heatmap */}
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <Layers className="h-5 w-5 mr-2 text-blue-600" />
                      {selectedCity} - {heatmapLayer.charAt(0).toUpperCase() + heatmapLayer.slice(1)} Density
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="relative h-96 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg p-4 overflow-hidden">
                      {/* City background */}
                      <div className="absolute inset-0 bg-gradient-to-br from-blue-50 to-purple-50 opacity-50"></div>
                        {/* Sample Dubai map background (shown only for Dubai Traffic Density) */}
                        {selectedCity === 'Dubai' && heatmapLayer === 'traffic' && (
                          <img
                            src={dubaiMap}
                            alt="Dubai map"
                            className="absolute inset-0 w-full h-full object-contain opacity-80 pointer-events-none"
                          />
                        )}
                      
                      {/* Placement dots with heatmap colors */}
                      <div className="relative h-full">
                        {currentCityData.placements.map((placement, index) => {
                          const x = 20 + (index * 15) + (Math.random() * 20);
                          const y = 20 + (Math.random() * 60);
                          const intensity = getHeatmapIntensity(getLayerValue(placement), getLayerMax());
                          const size = placement.premiumScore > 85 ? 'h-8 w-8' : placement.premiumScore > 70 ? 'h-6 w-6' : 'h-4 w-4';
                          
                          return (
                            <div
                              key={placement.id}
                              className="absolute group cursor-pointer"
                              style={{ left: `${x}%`, top: `${y}%` }}
                            >
                              <div className={`${size} bg-gradient-to-r ${intensity} rounded-full shadow-lg hover:scale-125 transition-all duration-200 border-2 border-white`}></div>
                              
                              {/* Placement tooltip */}
                              <div className="absolute top-10 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
                                <div className="bg-gray-900 text-white text-xs px-4 py-3 rounded-lg whitespace-nowrap shadow-lg">
                                  <div className="font-semibold mb-1">{placement.name}</div>
                                  <div>Type: {placement.type}</div>
                                  <div>Traffic: {placement.traffic.toLocaleString()}/day</div>
                                  <div>Investment: {formatCurrency(placement.investment)}</div>
                                  <div>Revenue: {formatCurrency(placement.revenue)}</div>
                                  <div>Walk-ins: {placement.walkIns}</div>
                                  <div>Home Orders: {placement.homeOrders}</div>
                                  <div>Premium Score: {placement.premiumScore}/100</div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      
                      {/* Legend */}
                      <div className="absolute bottom-4 left-4 bg-white p-3 rounded-lg shadow-lg">
                        <div className="text-sm font-semibold mb-2">{heatmapLayer.charAt(0).toUpperCase() + heatmapLayer.slice(1)} Intensity</div>
                        <div className="flex items-center space-x-2">
                          <span className="text-xs">Low</span>
                          <div className="flex space-x-1">
                            <div className="w-4 h-3 bg-gradient-to-r from-blue-200 to-blue-300 rounded"></div>
                            <div className="w-4 h-3 bg-gradient-to-r from-blue-400 to-blue-500 rounded"></div>
                            <div className="w-4 h-3 bg-gradient-to-r from-yellow-500 to-yellow-600 rounded"></div>
                            <div className="w-4 h-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded"></div>
                            <div className="w-4 h-3 bg-gradient-to-r from-red-600 to-red-700 rounded"></div>
                          </div>
                          <span className="text-xs">High</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* City Performance Summary */}
              <div className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">{selectedCity} Overview</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Total Spend:</span>
                        <span className="font-semibold">{formatCurrency(currentCityData.totalSpend)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Total Revenue:</span>
                        <span className="font-semibold text-green-600">{formatCurrency(currentCityData.totalRevenue)}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">ROI:</span>
                        <Badge className="bg-green-100 text-green-800">{currentCityData.roi}x</Badge>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Active Placements:</span>
                        <span className="font-semibold">{currentCityData.placements.length}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Walk-ins vs Home Orders</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {currentCityData.placements.slice(0, 3).map((placement, index) => {
                        const total = placement.walkIns + placement.homeOrders;
                        const walkInPercent = (placement.walkIns / total) * 100;
                        
                        return (
                          <div key={index} className="space-y-2">
                            <div className="flex justify-between text-sm">
                              <span className="font-medium">{placement.name.split(' - ')[0]}</span>
                              <span>{total} total</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-3">
                              <div 
                                className="bg-gradient-to-r from-blue-500 to-blue-600 h-3 rounded-full"
                                style={{ width: `${walkInPercent}%` }}
                              ></div>
                            </div>
                            <div className="flex justify-between text-xs text-gray-600">
                              <span>Walk-ins: {placement.walkIns} ({walkInPercent.toFixed(0)}%)</span>
                              <span>Home: {placement.homeOrders} ({(100 - walkInPercent).toFixed(0)}%)</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="placements" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {currentCityData.placements.map((placement) => (
                <Card key={placement.id} className="hover:shadow-lg transition-all duration-200">
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-lg">{placement.name}</CardTitle>
                        <Badge className="bg-blue-100 text-blue-800 text-xs mt-1">{placement.type}</Badge>
                      </div>
                      {getEfficiencyBadge(placement.efficiency)}
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="text-center p-2 bg-blue-50 rounded">
                          <Car className="h-4 w-4 mx-auto mb-1 text-blue-600" />
                          <div className="font-semibold">{placement.traffic.toLocaleString()}</div>
                          <div className="text-xs text-gray-600">Daily Traffic</div>
                        </div>
                        <div className="text-center p-2 bg-green-50 rounded">
                          <DollarSign className="h-4 w-4 mx-auto mb-1 text-green-600" />
                          <div className="font-semibold">{formatCurrency(placement.revenue)}</div>
                          <div className="text-xs text-gray-600">Revenue Gen.</div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div className="text-center p-2 bg-purple-50 rounded">
                          <Users className="h-4 w-4 mx-auto mb-1 text-purple-600" />
                          <div className="font-semibold">{placement.walkIns}</div>
                          <div className="text-xs text-gray-600">Walk-ins</div>
                        </div>
                        <div className="text-center p-2 bg-orange-50 rounded">
                          <ShoppingCart className="h-4 w-4 mx-auto mb-1 text-orange-600" />
                          <div className="font-semibold">{placement.homeOrders}</div>
                          <div className="text-xs text-gray-600">Home Orders</div>
                        </div>
                      </div>

                      <div className="pt-2 border-t">
                        <div className="flex justify-between items-center text-sm">
                          <span>Investment:</span>
                          <span className="font-semibold">{formatCurrency(placement.investment)}</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span>ROI:</span>
                          <span className="font-semibold text-green-600">{placement.efficiency}x</span>
                        </div>
                        <div className="flex justify-between items-center text-sm">
                          <span>Premium Score:</span>
                          <span className="font-semibold">{placement.premiumScore}/100</span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="delivery" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Order Delivery Analysis by Area</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {currentCityData.orderAreas.map((area, index) => {
                    const recommendation = getPremiumRecommendation(area);
                    
                    return (
                      <div key={index} className={`p-4 rounded-lg border-l-4 ${
                        recommendation.type === 'premium-opportunity' ? 'border-l-yellow-500 bg-yellow-50' :
                        recommendation.type === 'high-conversion' ? 'border-l-green-500 bg-green-50' :
                        'border-l-blue-500 bg-blue-50'
                      }`}>
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">{area.area}</h4>
                            <div className="flex space-x-4 text-sm text-gray-600 mt-1">
                              <span>Population: {area.population.toLocaleString()}</span>
                              <span>Orders: {area.orders.toLocaleString()}</span>
                              <span>Premium Sites: {area.premiumSites}</span>
                            </div>
                          </div>
                          <Badge className={`${
                            area.density > 0.3 ? 'bg-green-100 text-green-800' :
                            area.density > 0.15 ? 'bg-yellow-100 text-yellow-800' :
                            'bg-red-100 text-red-800'
                          }`}>
                            {(area.density * 100).toFixed(1)}% Order Density
                          </Badge>
                        </div>
                        
                        <div className="mb-3">
                          <div className="w-full bg-gray-200 rounded-full h-4">
                            <div 
                              className={`h-4 rounded-full ${
                                area.density > 0.3 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                                area.density > 0.15 ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' :
                                'bg-gradient-to-r from-red-500 to-red-600'
                              }`}
                              style={{ width: `${Math.min(area.density * 300, 100)}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="bg-white p-3 rounded border">
                          <div className="font-medium text-gray-900 mb-1">Recommendation</div>
                          <div className="text-sm text-gray-700 mb-2">{recommendation.message}</div>
                          <div className="text-sm font-medium text-blue-600">{recommendation.action}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="optimization" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Efficiency Analysis */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <Target className="h-5 w-5 mr-2 text-green-600" />
                    Efficiency Optimization
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="p-3 bg-green-50 border-l-4 border-l-green-500 rounded">
                      <div className="font-medium text-green-900">High Performance Areas</div>
                      <div className="text-sm text-green-700 mt-1">
                        • JBR Beach Walk: 3.09x ROI - Increase budget by 25%<br/>
                        • Dubai Mall: 2.78x ROI - Extend campaign duration
                      </div>
                    </div>
                    
                    <div className="p-3 bg-red-50 border-l-4 border-l-red-500 rounded">
                      <div className="font-medium text-red-900">Underperforming Areas</div>
                      <div className="text-sm text-red-700 mt-1">
                        • Al Khail Road: 1.86x ROI - Reduce spend by 40%<br/>
                        • Consider alternative creative or time slots
                      </div>
                    </div>
                    
                    <div className="p-3 bg-blue-50 border-l-4 border-l-blue-500 rounded">
                      <div className="font-medium text-blue-900">Projected Impact</div>
                      <div className="text-sm text-blue-700 mt-1">
                        Reallocation could improve overall ROI from 2.89x to 3.24x<br/>
                        Estimated additional revenue: {formatCurrency(1200000)}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Waste Reduction */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center">
                    <AlertTriangle className="h-5 w-5 mr-2 text-yellow-600" />
                    Waste Reduction Opportunities
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[
                      { 
                        area: 'Business Bay Bridge', 
                        waste: 125000, 
                        reason: 'Low conversion despite high traffic', 
                        action: 'Switch to awareness-focused creative' 
                      },
                      { 
                        area: 'Al Khail Road', 
                        waste: 98000, 
                        reason: 'Poor home delivery correlation', 
                        action: 'Reduce spend or relocate budget' 
                      },
                      { 
                        area: 'DIFC Premium Sites', 
                        waste: 75000, 
                        reason: 'Low order density in high-spend area', 
                        action: 'Focus on B2B messaging instead' 
                      }
                    ].map((item, index) => (
                      <div key={index} className="p-3 bg-yellow-50 border rounded">
                        <div className="flex justify-between items-start mb-2">
                          <div className="font-medium text-gray-900">{item.area}</div>
                          <Badge className="bg-red-100 text-red-800">{formatCurrency(item.waste)} wasted</Badge>
                        </div>
                        <div className="text-sm text-gray-700 mb-1">{item.reason}</div>
                        <div className="text-sm font-medium text-blue-600">{item.action}</div>
                      </div>
                    ))}
                    
                    <div className="pt-3 border-t">
                      <div className="text-center">
                        <div className="text-2xl font-bold text-red-600">{formatCurrency(298000)}</div>
                        <div className="text-sm text-gray-600">Total Potential Savings</div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* AI Insights Chatbot */}
      <AIInsightsChatbot />
    </div>
  );
};

export default UAEOutdoorAdvertising;